import React, { useRef, useState } from "react";
import PropTypes from "prop-types";

import classNames from "classnames";
import ReactTooltip from "react-tooltip";

import { Button } from "rhombus-react";

import { useClickOutsideHandler } from "utils/common";
import { useTooltipRebuild } from "hooks/useTooltipRebuild";

import "./VariableNameInput.css";

const MAX_LENGTH_VARIABLE_NAME = 100;

export default function VariableNameInput(props){
	const {
		value = "varName",
		tip = "Edit variable name",
		onChange,
		disabled,
		onBlur,
	} = props;

	const [isEditing, setIsEditing] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const inputRef = useRef(null);

	const variableClassName = classNames({
		"variable-name": true,
		disabled,
	});

	useTooltipRebuild(isEditing);

	function onSave(){
		setIsEditing(false);
		setErrorMessage("");
		onChange?.(inputRef.current?.value);
		onBlur?.();
	}

	function onCancel(){
		setErrorMessage("");
		setIsEditing(false);
		onBlur?.();
	}

	const [containerRef] = useClickOutsideHandler(onCancel);

	function handleChange(e){
		const currentValue = e.target?.value;
		if(!currentValue){
			setErrorMessage("Variable name can't be empty");
		} else if(currentValue?.length > MAX_LENGTH_VARIABLE_NAME){
			setErrorMessage("Variable name can't be longer than 100 characters");
		} else if(/[^a-zA-Z0-9_]/.test(currentValue)){
			setErrorMessage("Variable name can only contain letters, numbers, and underscores");
		} else {
			setErrorMessage("");
		}
	}

	function handleKeyDown(e){
		if(e.key === "Escape"){
			e.stopPropagation();
			onCancel();
		}

		if(e.key === "Enter" && !errorMessage){
			e.stopPropagation();
			onSave();
		}
	}

	const wrapperClassName = classNames({
		"variable-name-editor": true,
		"has-error": !!errorMessage,
	});

	const handleEditClick = () => {
		if (!disabled) {
			setIsEditing(true);
		}
	};

	if(isEditing){
		return (
			<div className="variable-name-container">
				<div
					className={wrapperClassName}
					ref={containerRef}
					data-tip
					data-for={"variable-name-error"}
				>
					<div>
						<input
							autoFocus
							defaultValue={value}
							ref={inputRef}
							onChange={handleChange}
							onKeyDown={handleKeyDown}
						/>
					</div>
					<div className="buttons">
						<Button
							theme="default"
							size="small"
							onClick={onCancel}
						>
							Cancel
						</Button>
						<Button
							theme="green"
							size="small"
							onClick={onSave}
							disabled={!!errorMessage}
						>
							Apply
						</Button>
					</div>
				</div>
				<ReactTooltip
					effect="solid"
					place="top"
					type="error"
					id="variable-name-error"
					getContent={() => errorMessage}
				/>
			</div>
		);
	}

	return (
		<div
			className="variable-name-container">
			<span
				className={variableClassName}
				data-tip={tip}
				onClick={handleEditClick}
			>
				{value}
			</span>
		</div>
	);
}

VariableNameInput.displayName = "VariableNameInput";
VariableNameInput.propTypes = {
	value: PropTypes.string,
	onChange: PropTypes.func,
};
